import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { userRecordPictureSelector } from 'src/app/user/store/user.selectors';
import { tap, takeUntil, map, mergeMap, switchMap, filter, catchError, finalize, take, first } from 'rxjs/operators';
import { CreateUserRecordAction, CreateUserRecordErrorAction, CreateUserRecordSuccessAction, SearchUsersAction } from 'src/app/user/store/user.actions';
import { TranslateService } from '@ngx-translate/core';
import { emailRegex } from 'src/app/global/validators/match.validator';
import { UserService } from '../../service/user.abstract.service';
import { ProfileField } from '@bciti/types';
import { Address, CreateUserRecordPayload } from '../../user.model';
import { profileOptionsSelector, organisationConfigurationSelector, organisationTagSelector } from 'src/app/global/store/global.selectors';
import { formatDateOnly } from 'src/app/global/utils/date/date.utils';
import { environment } from '../../../../environments/environment';
import { authConfigIdSelector } from 'src/app/auth/store/auth.selectors';
import { ResidenceService } from 'src/app/residence/service/residence.abstract.service';
import { ResidenceSubType } from 'src/app/residence/residence.model';
import { GeoidAddressModalComponent } from 'src/app/global/components/forms/geoid-address/modal/geoid-address-modal.component';

@Component({
    selector: 'app-user-create',
    templateUrl: './user-create.component.html',
    styleUrls: ['./user-create.component.scss'],
})
export class UserCreateComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    public picture$ = this.store.select(userRecordPictureSelector);

    public profileForm: FormGroup;

    public loading = false;

    public profileLoaded = true;

    public t: any = false;

    public payload;

    public hiddenFields: ProfileField[] = [];

    public profileField = ProfileField;

    public pendingAddress: Address | null = null;

    public pendingAddressSubType: ResidenceSubType = 'RESIDENT';

    public orgTag: string;

    constructor(
        private formBuilder: FormBuilder,
        private store: Store<State>,
        private actions$: Actions,
        private messageService: MessageService,
        private translateService: TranslateService,
        private userService: UserService,
        private residenceService: ResidenceService,
        private dialog: MatDialog,
        private router: Router,
    ) {}

    get firstName(): AbstractControl {
        return this.profileForm.get('firstName');
    }

    get lastName(): AbstractControl {
        return this.profileForm.get('lastName');
    }

    get sex(): AbstractControl {
        return this.profileForm.get('sex');
    }

    get profilePicture(): AbstractControl {
        return this.profileForm.get('profilePicture');
    }

    get phone(): AbstractControl {
        return this.profileForm.get('phone');
    }

    get picture(): AbstractControl {
        return this.profileForm.get('picture');
    }

    get secPhone(): AbstractControl {
        return this.profileForm.get('secPhone');
    }

    get secPhoneExt(): AbstractControl {
        return this.profileForm.get('secPhoneExt');
    }

    get cellPhone(): AbstractControl {
        return this.profileForm.get('cellPhone');
    }

    get birthDate(): AbstractControl {
        return this.profileForm.get('birthDate');
    }

    get email(): AbstractControl {
        return this.profileForm.get('email');
    }

    public validateDateIsInPast(c: FormControl): { [key: string]: { [key: string]: boolean } } | null {
        if (c.value) {
            const today = new Date().toISOString();
            return new Date(c.value).toISOString() < today ? null : { validateDateIsInPast: { valid: false } };
        }
        return null;
    }

    public ngOnInit(): void {
        this.translateService
            .get('pages.user-create')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));

        combineLatest([this.store.select(profileOptionsSelector), this.store.select(organisationConfigurationSelector), this.store.select(organisationTagSelector)])
            .pipe(
                takeUntil(this.destroy$),
                map(([profileOptions, orgConfig, orgTag]) => {
                    this.orgTag = orgTag;
                    this.hiddenFields = profileOptions?.hiddenFields ?? [];
                    this.initializeProfileForm();
                    if (orgConfig?.dummyAccount && this.profileForm.controls.email) {
                        this.profileForm.controls.email.clearValidators();
                        this.profileForm.controls.email.setValidators(Validators.pattern(emailRegex));
                        this.profileForm.controls.email.updateValueAndValidity();
                    }
                }),
                tap(() => (this.profileLoaded = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(CreateUserRecordAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(CreateUserRecordSuccessAction),
                first(),
                mergeMap(({ id }) =>
                    this.patchProfileFields(id).pipe(
                        mergeMap(() => this.createPendingResidence(id)),
                        mergeMap(() => this.uploadPicture(id)),
                        tap(() => this.onCreateSuccess(id)),
                        catchError(() => {
                            this.messageService.error('User created but profile update failed. You can update the profile from the edit page.');
                            this.router.navigate(['/user-update', id]);
                            return of(undefined);
                        }),
                        finalize(() => (this.loading = false)),
                    ),
                ),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(CreateUserRecordErrorAction),
                tap(() => (this.loading = false)),
                tap(({ error }) => {
                    if (error?.status === 409) {
                        this.messageService.error(this.t?.errors?.alreadyUsed);
                    } else {
                        this.messageService.error(this.t?.errors?.notRegistered, this.t?.errors.caughtError);
                    }
                }),
            )
            .subscribe();
    }

    public initializeProfileForm() {
        this.profileForm = this.formBuilder.group(
            {
                firstName: new FormControl('', [Validators.required, Validators.minLength(2)]),
                lastName: new FormControl('', [Validators.required, Validators.minLength(2)]),
                sex: new FormControl('', []),
                profilePicture: new FormControl(''),
                birthDate: new FormControl('', [this.validateDateIsInPast]),
                picture: new FormControl(''),
                phone: new FormControl('', [Validators.minLength(14), Validators.maxLength(14)]),
                secPhone: new FormControl('', [Validators.minLength(14), Validators.maxLength(14)]),
                secPhoneExt: new FormControl(''),
                cellPhone: new FormControl('', [Validators.minLength(14), Validators.maxLength(14)]),
                email: new FormControl('', [Validators.pattern(emailRegex), Validators.required]),
            },
            {
                updateOn: 'blur',
            },
        );

        if (this.hiddenFields) {
            this.removeHiddenFields();
        }
    }

    removeHiddenFields() {
        const controls = Object.keys(this.profileForm.controls);
        controls.forEach((controlName) => {
            this.hiddenFields.forEach((hiddenField) => {
                if (controlName.toUpperCase() === (hiddenField as string) && this.profileForm.get(controlName) && hiddenField !== ProfileField.EMAIL) {
                    this.profileForm.removeControl(controlName);
                }
            });
        });
        this.profileForm.updateValueAndValidity();
    }

    public displayField(hiddenField: ProfileField): boolean {
        return !this.hiddenFields.includes(hiddenField);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public save(): void {
        this.profileForm.markAllAsTouched();

        if (this.profileForm.valid) {
            this.store
                .select(authConfigIdSelector)
                .pipe(take(1))
                .subscribe((clientId) => {
                    const payload: CreateUserRecordPayload = {
                        email: this.email.value,
                        firstName: this.firstName.value,
                        lastName: this.lastName.value,
                        orgId: environment.OIDC_ORG_ID,
                        clientId: clientId || undefined,
                    };
                    this.store.dispatch(CreateUserRecordAction({ payload }));
                });
        }
    }

    private buildProfilePayload(): Record<string, any> {
        const payload: any = {};
        if (this?.phone?.value) payload.phone = this.phone.value;
        if (this?.secPhone?.value) payload.secPhone = this.secPhone.value;
        if (this?.secPhoneExt?.value) payload.secPhoneExt = this.secPhoneExt.value;
        if (this?.cellPhone?.value) payload.cellPhone = this.cellPhone.value;
        if (this?.birthDate?.value) payload.birthDate = formatDateOnly(this.birthDate.value);
        if (this?.sex?.value) payload.sex = this.sex.value;
        return payload;
    }

    private patchProfileFields(userId: string): Observable<any> {
        const payload = this.buildProfilePayload();
        if (Object.keys(payload).length === 0) return of(null);
        return this.userService.updateUserRecordById(userId, payload);
    }

    public openAddAddressDialog(): void {
        this.dialog
            .open(GeoidAddressModalComponent, { data: { showSubTypeSelector: true } })
            .afterClosed()
            .pipe(
                take(1),
                filter((result) => !!result),
            )
            .subscribe((result) => {
                this.pendingAddress = result.address;
                this.pendingAddressSubType = result.subType || 'RESIDENT';
            });
    }

    public removePendingAddress(): void {
        this.pendingAddress = null;
        this.pendingAddressSubType = 'RESIDENT';
    }

    // The address is created as a Residence tied to the new user, the same way user-profile does it,
    // so it goes through the residence/verification module instead of the legacy single-address field.
    private createPendingResidence(userId: string): Observable<any> {
        if (!this.pendingAddress) return of(null);

        return this.store.select(organisationTagSelector).pipe(
            take(1),
            switchMap((orgTag) =>
                this.residenceService.createResidence({
                    userId,
                    address: this.pendingAddress,
                    organisation: orgTag,
                    subType: this.pendingAddressSubType,
                }),
            ),
            catchError(() => {
                this.messageService.error(this.translateService.instant('pages.residences.toast.error'));
                return of(null);
            }),
        );
    }

    private uploadPicture(userId: string): Observable<any> {
        if (!this.picture.value) return of(null);
        return this.userService.uploadProfilePictureById(userId, this.picture.value).pipe(mergeMap((url) => this.userService.updateUserRecordById(userId, { picture: url })));
    }

    private onCreateSuccess(userId: string): void {
        this.messageService.success(this?.t?.createUserRecordSuccess);
        this.store.dispatch(SearchUsersAction());
        this.router.navigate(['/user-update', userId]);
    }

    public markAllAsUntouched(): void {
        this?.firstName?.markAsUntouched();
        this?.lastName?.markAsUntouched();
        this?.sex?.markAsUntouched();
        this?.phone?.markAsUntouched();
        this?.secPhone?.markAsUntouched();
        this?.secPhoneExt?.markAsUntouched();
        this?.cellPhone?.markAsUntouched();
        this?.birthDate?.markAsUntouched();
        this?.picture?.markAsUntouched();
        this?.email?.markAsUntouched();
    }
}
